<template>
  <div>
    <v-row
      v-if="sortedItems.length > 0"
      no-gutters
      style="flex-wrap: nowrap; width: 100%"
      class="border-bottom"
    >
      <v-card
        v-if="selectable"
        class="d-flex align-center justify-start pl-1"
        :height="headerHeight"
        :width="checkboxWidth"
        flat
      >
        <v-simple-checkbox
          :color="!indeterminate ? 'primary' : ''"
          :indeterminate="indeterminate"
          :ripple="false"
          :value="allSelected"
          @input="$emit('select-all')"
        />
      </v-card>

      <template v-for="(column, index) in columns">
        <v-card
          :key="index"
          class="d-flex align-center justify-start pl-1"
          :height="headerHeight"
          :minWidth="column.width"
          :width="column.width"
          flat
          @click="toggleSort(column.prop)"
        >
          <span
            class="body-label-xs d-flex align-center"
            :style="{ height: '100%' }"
          >
            <slot :name="`${column.prop}Header`">
              {{ column.label }}
            </slot>
            <v-icon v-if="sortColumn === column.prop">
              {{ sortOrder === 'asc' ? 'mdi-menu-up' : 'mdi-menu-down' }}
            </v-icon>
          </span>
        </v-card>
      </template>
    </v-row>

    <vue-virtual-table
      v-if="sortedItems.length > 0"
      :data="sortedItems"
      :config="tableConfig"
      :height="bodyHeight"
      :minHeight="minHeight"
      class="base-table"
      :itemHeight="rowHeight"
      :minWidth="minWidth"
      :maxHeight="maxHeight"
      @click="$emit('row-click', $event)"
    >
      <template v-if="selectable" slot="selectCommon" slot-scope="scope">
        <v-row
          no-gutters
          justify="start"
          align="center"
          style="flex: 0 0 auto; width: 30px !important; height: 100%"
        >
          <v-simple-checkbox
            color="primary"
            :ripple="false"
            :value="isSelected(scope.row)"
            @input="$emit('select-row', scope.row)"
          />
        </v-row>
      </template>

      <template
        v-for="(column, index) in columns"
        :slot="column.slot || `${column.prop}Common`"
        slot-scope="scope"
      >
        <v-row
          :key="index"
          no-gutters
          justify="start"
          class="d-flex align-center justify-start"
          :style="`
            height: 100%;
            width: ${column.width - 10}px;
            min-width: ${column.width - 10}px;
            max-width: ${column.width - 10}px;
            overflow: hidden;
          `"
          @click="$emit('row-click', scope.row)"
        >
          <slot :name="column.slot || `${column.prop}Common`" :row="scope.row">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="text-truncate"
                  :style="`
                display: block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                width: 100%;
                text-align:start
              `"
                  :title="scope.row[column.prop]"
                >
                  {{ scope.row[column.prop] }}
                </span>
              </template>
              <span> {{ scope.row[column.prop] }}</span>
            </v-tooltip>
          </slot>
        </v-row>
      </template>

      <template
        v-if="menuOptions && menuOptions.length > 0"
        slot="actionsCommon"
        slot-scope="scope"
      >
        <v-row no-gutters justify="end" align="center" style="height: 100%">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="iconElement" icon small v-bind="attrs" v-on="on">
                <v-icon small>panda-menu-action</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-0" style="border-radius: 8px">
              <v-list-item
                v-for="(option, index) in filteredMenuOptions(scope.row)"
                :key="index"
                @click="handleMenuClick(option.action, scope.row)"
                :style="option.style"
              >
                <v-list-item-title
                  ><span class="body-bold-smaller" :style="option.textColor">{{
                    option.label
                  }}</span></v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>
        </v-row>
      </template>
    </vue-virtual-table>
    <section
      v-else
      class="align-center justify-center"
      style="height: 100%; display: grid; min-height: 450px"
    >
      <v-row
        class="d-flex flex-column"
        no-gutters
        justify="center"
        align="center"
      >
        <img
          class="emptyStateTitle mb-4"
          src="@/assets/videosEmptyState.svg"
          max-width="240"
        />
        <span class="body-regular-large textType3--text">
          {{ textTitleEmpty }}</span
        >
        <span
          class="body-regular-small textType6--text"
          style="text-align: center; white-space: pre-line"
        >
          {{ textDescriptionEmpty }}
        </span>
      </v-row>
    </section>
  </div>
</template>

<script>
import VueVirtualTable from 'vue-virtual-table'

export default {
  components: {
    VueVirtualTable,
  },
  data() {
    return {
      sortColumn: null,
      sortOrder: 'asc',
      sortedItems: [],
    }
  },
  props: {
    textTitleEmpty: {
      type: String,
      default: 'Nenhum item encontrado',
    },
    textDescriptionEmpty: {
      type: String,
      default: 'Nenhum item encontrado',
    },
    items: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      required: true,
      validator: (cols) => cols.every((c) => c.prop && c.label),
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    headerHeight: {
      type: [Number, String],
      default: 45,
    },
    maxHeight: {
      type: [Number, String],
      default: 450,
    },
    minHeight: {
      type: [Number, String],
      default: 450,
    },
    rowHeight: {
      type: [Number, String],
      default: 48,
    },
    bodyHeight: {
      type: [Number, String],
      default: 450,
    },
    minWidth: {
      type: [Number, String],
      default: 770,
    },
    checkboxWidth: {
      type: [Number, String],
      default: 38,
    },
    menuWidth: {
      type: [Number, String],
      default: 50,
    },
    menuOptions: {
      type: Array,
      default: () => [],
      validator: (options) => options.every((opt) => opt.label && opt.action),
    },
  },
  computed: {
    tableConfig() {
      const config = this.columns.map((column) => ({
        prop: '_action',
        name: column.label,
        actionName: column.slot || `${column.prop}Common`,
        width: column.width || 150,
        alignItems: column.align || 'start',
      }))

      if (this.selectable) {
        config.unshift({
          prop: '_action',
          name: 'action',
          actionName: 'selectCommon',
          width: this.checkboxWidth,
          alignItems: 'center',
        })
      }

      if (this.menuOptions && this.menuOptions.length > 0) {
        config.push({
          prop: '_action',
          name: 'Ações',
          actionName: 'actionsCommon',
          width: this.menuWidth,
          alignItems: 'center',
        })
      }

      return config
    },
    indeterminate() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.items.length
      )
    },
    allSelected() {
      return (
        this.selectedItems.length === this.items.length && this.items.length > 0
      )
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.sortedItems = [...newItems]
      },
    },
  },
  methods: {
    filteredMenuOptions(row) {
      return this.menuOptions.filter((option) => {
        return typeof option.condition === 'function'
          ? option.condition(row)
          : true
      })
    },
    isSelected(item) {
      return this.selectedItems.some((selected) => selected.id === item.id)
    },
    toggleSort(prop) {
      if (this.sortColumn === prop) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = prop
        this.sortOrder = 'asc'
      }

      this.sortData()
    },
    sortData() {
      this.sortedItems.sort((a, b) => {
        const valA = a[this.sortColumn]
        const valB = b[this.sortColumn]

        if (valA == null || valB == null) return 0

        if (typeof valA === 'string' && typeof valB === 'string') {
          return this.sortOrder === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA)
        }

        return this.sortOrder === 'asc'
          ? valA > valB
            ? 1
            : valA < valB
            ? -1
            : 0
          : valA < valB
          ? 1
          : valA > valB
          ? -1
          : 0
      })
    },
    handleMenuClick(action, row) {
      this.$emit('menu-action', { action, row })
    },
  },
}
</script>
<style lang="scss">
.t-header {
  display: none;
}
</style>
<style lang="scss" scoped>
.border-bottom {
  border-bottom: 1px solid varFigma(neutral-95-lightest);
}

.base-table {
  border: inherit !important;
}

::v-deep .item-cell:nth-child(1) {
  flex: 0 0 30px !important;
  max-width: 56px !important;
  min-width: 38px !important;
}

::v-deep .item-cell {
  border: inherit !important;
  display: block !important;
  flex: inherit !important;
}

::v-deep .item-line {
  background: inherit !important;
}

::v-deep .item-line:hover {
  background: varFigma(neutral-99) !important;
}

::v-deep .v-menu__content {
  border-radius: 8px;
}

.iconElement {
  border-radius: 4px;
  max-height: 24px;
  max-width: 24px;
  min-width: 24px;
  min-height: 24px;
  align-self: center;
  justify-content: center;
  display: flex;
  background: varFigma(neutral-100-white);
}

.iconElement:hover {
  background: varFigma(neutral-90);
}
</style>
